import { memo } from 'react';
import { Group, Circle, Line, Ellipse, Arc } from 'react-konva';

/**
 * StickmanCharacter - plně animovatelný panáček z čar
 * Props pro animaci: headAngle, leftArmAngle, rightArmAngle, leftForearmAngle, rightForearmAngle,
 *   leftLegAngle, rightLegAngle, leftShinAngle, rightShinAngle, mouthWidth, eyebrowAngle
 */
export const StickmanCharacter = memo(function StickmanCharacter({
  x, y, scaleX, scaleY, rotation, opacity, fill,
  headAngle, leftArmAngle, rightArmAngle, leftForearmAngle, rightForearmAngle,
  leftLegAngle, rightLegAngle, leftShinAngle, rightShinAngle,
  mouthWidth, eyebrowAngle,
  onClick, onDragEnd, onTransformEnd, draggable
}) {
  const color = fill || '#222222';
  const sw = 4; // stroke width
  const ha = headAngle || 0;
  const la = leftArmAngle || 0;
  const ra = rightArmAngle || 0;
  const lfa = leftForearmAngle || 0;
  const rfa = rightForearmAngle || 0;
  const ll = leftLegAngle || 0;
  const rl = rightLegAngle || 0;
  const lsh = leftShinAngle || 0;
  const rsh = rightShinAngle || 0;
  const mw = mouthWidth || 0;
  const eba = eyebrowAngle || 0;

  return (
    <Group
      x={x} y={y}
      scaleX={scaleX || 1} scaleY={scaleY || 1}
      rotation={rotation || 0}
      opacity={opacity ?? 1}
      draggable={draggable}
      onClick={() => onClick?.()}
      onTap={() => onClick?.()}
      onDragEnd={(e) => onDragEnd?.({ x: e.target.x(), y: e.target.y() })}
      onTransformEnd={(e) => {
        const node = e.target;
        onTransformEnd?.({ x: node.x(), y: node.y(), scaleX: node.scaleX(), scaleY: node.scaleY(), rotation: node.rotation() });
      }}
    >
      {/* Shadow */}
      <Ellipse x={0} y={82} radiusX={18} radiusY={4} fill="#000" opacity={0.12} />

      {/* Left leg - thigh */}
      <Group rotation={ll} x={-6} y={40}>
        <Line points={[0, 0, 0, 28]} stroke={color} strokeWidth={sw} lineCap="round" />
        {/* Left shin */}
        <Group rotation={lsh} x={0} y={28}>
          <Line points={[0, 0, 0, 26]} stroke={color} strokeWidth={sw} lineCap="round" />
          {/* Foot */}
          <Line points={[0, 26, 8, 26]} stroke={color} strokeWidth={sw} lineCap="round" />
        </Group>
      </Group>

      {/* Right leg - thigh */}
      <Group rotation={rl} x={6} y={40}>
        <Line points={[0, 0, 0, 28]} stroke={color} strokeWidth={sw} lineCap="round" />
        {/* Right shin */}
        <Group rotation={rsh} x={0} y={28}>
          <Line points={[0, 0, 0, 26]} stroke={color} strokeWidth={sw} lineCap="round" />
          {/* Foot */}
          <Line points={[0, 26, 8, 26]} stroke={color} strokeWidth={sw} lineCap="round" />
        </Group>
      </Group>

      {/* Body / spine */}
      <Line points={[0, -12, 0, 40]} stroke={color} strokeWidth={sw} lineCap="round" />

      {/* Left arm - upper */}
      <Group rotation={la} x={0} y={-4}>
        <Line points={[0, 0, -22, 16]} stroke={color} strokeWidth={sw} lineCap="round" />
        {/* Left forearm */}
        <Group rotation={lfa} x={-22} y={16}>
          <Line points={[0, 0, -14, 18]} stroke={color} strokeWidth={sw} lineCap="round" />
          {/* Hand */}
          <Circle x={-14} y={18} radius={3} fill={color} />
        </Group>
      </Group>

      {/* Right arm - upper */}
      <Group rotation={ra} x={0} y={-4}>
        <Line points={[0, 0, 22, 16]} stroke={color} strokeWidth={sw} lineCap="round" />
        {/* Right forearm */}
        <Group rotation={rfa} x={22} y={16}>
          <Line points={[0, 0, 14, 18]} stroke={color} strokeWidth={sw} lineCap="round" />
          {/* Hand */}
          <Circle x={14} y={18} radius={3} fill={color} />
        </Group>
      </Group>

      {/* Head */}
      <Group rotation={ha} x={0} y={-12}>
        <Circle x={0} y={-16} radius={14} fill="#fafafa" stroke={color} strokeWidth={sw - 1} />

        {/* Eyes */}
        <Circle x={-5} y={-18} radius={2.5} fill={color} />
        <Circle x={5} y={-18} radius={2.5} fill={color} />

        {/* Eyebrows - animated */}
        <Line points={[-9, -24, -2, -24 + eba]} stroke={color} strokeWidth={2} lineCap="round" />
        <Line points={[9, -24, 2, -24 + eba]} stroke={color} strokeWidth={2} lineCap="round" />

        {/* Mouth - animated width for expressions */}
        {mw >= 0 ? (
          <Arc x={0} y={-12} innerRadius={0} outerRadius={Math.max(2, Math.abs(mw))} angle={180} rotation={-90} fill={color} />
        ) : (
          <Arc x={0} y={-10} innerRadius={0} outerRadius={Math.max(2, Math.abs(mw))} angle={180} rotation={90} fill={color} />
        )}
      </Group>
    </Group>
  );
});
